import { Alert, Box, CircularProgress, Container, Paper } from "@mui/material";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import MemberForm from "@/components/organization/Member/Form";
import { useFindMeQuery, useUpdateProfileMutation } from "@/libs/generated/hooks";
import type { MemberFormSchemaType } from "@/schemas/organization-form/member.schema";

function Profile() {
  const { t } = useTranslation();

  const { data, isPending, error } = useFindMeQuery();
  const [updateProfile, { isPending: updateLoading, error: updateError }] =
    useUpdateProfileMutation();

  const onSubmit = async (formData: MemberFormSchemaType) => {
    if (!data?.findMe?.id) {
      toast.error(t("network.errors.400"), {
        position: "bottom-right",
      });
      return;
    }

    try {
      const result = await updateProfile({
        UpdateProfileInput: {
          id: data.findMe.id,
          assigneeFirstName: formData.assigneeFirstName,
          assigneeLastName: formData.assigneeLastName,
          assigneePhone: formData.assigneePhone,
          assigneeFunction: formData.assigneeFunction,
          assigneeService: formData.assigneeService,
        },
      });

      if (result.data?.updateProfile) {
        toast.success(t("network.organization.member.updated"), {
          position: "bottom-right",
        });
      }
    } catch (_err) {
      // Error is handled by the mutation hook and displayed in the UI
    }
  };

  if (isPending) {
    return (
      <Container
        maxWidth="lg"
        sx={{
          display: "grid",
          placeContent: "center",
          paddingTop: "100px",
        }}
      >
        <Box display="flex" justifyContent="center" alignItems="center">
          <CircularProgress />
          <span style={{ marginLeft: 8 }}>{t("common.loading")}</span>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container
        maxWidth="lg"
        sx={{
          display: "grid",
          placeContent: "center",
          paddingTop: "100px",
        }}
      >
        <Alert severity="error">{error.message || t("network.errors.unknown")}</Alert>
      </Container>
    );
  }
  return (
    <Container
      maxWidth="lg"
      sx={{
        display: "grid",
        placeContent: "center",
        paddingTop: "100px",
      }}
    >
      <Paper
        component="div"
        elevation={4}
        sx={{
          backgroundColor: "#E7E6F7",
          borderRadius: 1,
          padding: "20px",
          height: "100%",
        }}
      >
        {updateError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {updateError.message || t("network.errors.unknown")}
          </Alert>
        )}

        {updateLoading && (
          <Box display="flex" justifyContent="center" alignItems="center" mb={2}>
            <CircularProgress size={20} />
            <span style={{ marginLeft: 8 }}>{t("common.loading")}</span>
          </Box>
        )}

        <MemberForm
          disabledFields={["assigneeEmail"]}
          title="user-info"
          unUsedFields={["assigneeRank"]}
          values={
            data?.findMe
              ? {
                  ...data.findMe,
                  assigneeRank: Number(data.findMe.assigneeRank),
                }
              : undefined
          }
          onSubmit={onSubmit}
          label={t("common.actions.save")}
        />
      </Paper>
    </Container>
  );
}

export default Profile;
